import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid2 as Grid,
  Paper,
  Alert,
  useMediaQuery,
} from "@mui/material";
import MosaicPlaceholder from "../components/MosaicPlaceholder";
import theme from "../styles/theme";
import RegistrationForm from "../components/RegistrationForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import registrationService from "../services/registrationService";
import CourseService from "../services/courseService";
import useNotification from "../hooks/useNotification";
import Course from "../models/Course";
import { styled } from "@mui/material/styles";
import { PDFDocument } from "pdf-lib";
import CourseBox from "../components/CourseBox";
import LoginForm from "../components/LoginForm";
import participantService from "../services/participantService";

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: "auto", // This pushes the button to the bottom
  backgroundColor: '#00537E'
}));

const scrollIntoView = (elementId: string) => {
  const elem = document.getElementById(elementId);
  if (elem) {
    var top = elem.offsetTop; //Getting Y of target element
    window.scrollTo(0, top);
  }
}

/*
  TODO:
    Get list of online courses
    Compare courses against those to ensure registration/login to the right course.
*/
const OnlineCoursePage: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const participantId = useRef<string>("");
  const emailLock = useRef<boolean>(false);
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false);
  const [isLoginFormOpen, setIsLoginFormOpen] = useState(false);
  const [courseToRegisterFor, setCourseToRegisterFor] = useState<Course | undefined>(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchCoursesWithLog = async () => {
    const result = await CourseService.fetchCourses({
      limit: 500,
      visible: true,
    });
    if (result && result.courses && Array.isArray(result.courses)) {
      return result.courses;
    } else {
      console.error("courses is not an array or data is missing:", result);
      return [];
    }
  };

  const { data: onlineCourses } = useQuery<
    Course[],
    Error,
    Course[],
    ["courses"]
  >({
    queryKey: ["courses"],
    queryFn: fetchCoursesWithLog,
    select: (courseData: any) => {
      if (courseData) {
        return courseData.filter(
          (course: Course) => course.visible && course.type === "online"
        );
      } else {
        console.error(
          "courses is not an array or data is missing:",
          courseData
        );
        return [];
      }
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Mutation for course registration
  const registrationMutation = useMutation({
    mutationFn: (data: {
      courseId: number | null;
      formData: any;
      code: string | undefined;
    }) =>
      registrationService.registerForCourse(
        data.courseId,
        data.formData,
        data.code
      ),
    onSuccess: (response) => {
      // Only handle 2xx success here
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      showNotification(
        "Registration request submitted successfully",
        "success"
      );
      handleRegistrationFormClose();
    },
    onError: (error: any) => {
      // Handle known error codes
      if (error?.response) {
        if (error.response.status === 409) {
          showNotification(
            `You are already registered for this course. Current status: ${error.response.data?.status || "Registered"
            }`,
            "info"
          );
        } else if (error.response.status === 403) {
          showNotification(
            "Course is full. Please try another course or contact the administrator.",
            "error"
          );
        } else {
          showNotification(
            "Failed to register for the course. Please try again.",
            "error"
          );
        }
      } else {
        showNotification(
          "Failed to register for the course. Please try again.",
          "error"
        );
      }
      handleRegistrationFormClose();
    },
  });

  const handleRegister = (courseToRegister: string) => {
    if (onlineCourses) {
      const relCourse = onlineCourses.filter(item => item.name.toLocaleLowerCase().includes(courseToRegister.toLocaleLowerCase()))[0]
      if (relCourse) {
        setCourseToRegisterFor(relCourse)
        setIsRegistrationFormOpen(true);
      }
    }
  };

  const handleRegistrationFormClose = () => {
    setCourseToRegisterFor(undefined)
    setIsRegistrationFormOpen(false);
  };

  const handleLoginFormOpen = (courseToLogin: string) => {
    if (onlineCourses) {
      const relCourse = onlineCourses.filter(item => item.name.toLocaleLowerCase().includes(courseToLogin.toLocaleLowerCase()))[0]
      if (relCourse) {
        setCourseToRegisterFor(relCourse)
        setIsLoginFormOpen(true);
      }
    }
  }

  const handleLoginFormClose = () => {
    setCourseToRegisterFor(undefined);
    setIsLoginFormOpen(false);
  }

  const handleRegistrationFormSubmit = async (formData: any) => {
    registrationMutation.mutate({
      courseId: courseToRegisterFor ? courseToRegisterFor.id : null,
      formData: formData.submissionData,
      code: "",
    });
  };

  const startCourse = () => {
    setIsLoginFormOpen(false)
    let handler: Window | null = null;
    if (courseToRegisterFor != null && courseToRegisterFor != undefined) {
      //Dispatcher
      if (courseToRegisterFor.id == 273) {
        handler = window.open(
          "/Course/Amtrak RailSafe Dispatcher Training Course/story.html",
          "ModuleFrame",
          "width=" +
          1024 +
          ",height=" +
          600 +
          ",status=yes,scrollbars=auto,resizable=yes,left=0,top=0,screenx=0,screeny=0"
        );

        const poll = setInterval(() => {
          try {
            const player = (handler as any).GetPlayer();
            if (!player) return
            const score = player.GetVar("userScorePercent");
            console.log("Score:", score);

              if (score && score >= 80) {
                  localStorage.setItem("OnlineCourseStatus", "COMPLETE");
                  clearInterval(poll);
              }
          } catch (e) {
            //Something went wrong.
            console.log(e);
          }
        }, 1000)

        handler?.addEventListener('close', () => {
          clearInterval(poll)
        })
      }

      //Parter
      if (courseToRegisterFor.id == 158) {
        handler = window.open(
          "/Course/wbt/Amtrak_WBT_1.html",
          "ModuleFrame",
          "width=" +
          1024 +
          ",height=" +
          600 +
          ",status=yes,scrollbars=auto,resizable=yes,left=0,top=0,screenx=0,screeny=0"
        );
      }
    }

    if (handler != null) {
      //Dispatcher_Cert.pdf
      handler.addEventListener("unload", (event) => {
        const result = localStorage.getItem("OnlineCourseStatus");
        const tempId = localStorage.getItem("CurrentParticipantId");
        if(tempId) {
          participantId.current = tempId;
        } else {
          participantId.current = "";
        }
        //Build and send Certification
        if (courseToRegisterFor && !emailLock.current && result === "COMPLETE") {
          emailLock.current = true;
          var certName = "";
          if (courseToRegisterFor.id == 158) {
            certName = "/Course/wbt/Webinar_Cert.pdf"
          }

          if (courseToRegisterFor.id == 273) {
            certName = "/Course/Amtrak RailSafe Dispatcher Training Course/Dispatcher_Cert.pdf";
          }

          fetch(certName).then(async (res) => {
            const existingPdfBytes = await res.arrayBuffer();
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const form = pdfDoc.getForm();

            const reg =
              await registrationService.getRegistrationDataForCourseAndParticipant(
                courseToRegisterFor.id.toString(),
                participantId.current
              );

            const nameField = form.getTextField("studentName");
            const dateField = form.getTextField("studentDate");
            if (reg != undefined && reg.participant != undefined) {
              nameField.setText(
                reg.participant.firstName + " " + reg.participant.lastName
              );
            }
            const currentDate = new Date(Date.now());
            dateField.setText(currentDate.toLocaleDateString());

            const pdfBytes = await pdfDoc.saveAsBase64();

            registrationService
              .updateOnlineCourseCompleteByEmail(
                courseToRegisterFor.id.toString(),
                reg.id,
                pdfBytes
              )
              .then((result) => {
                participantId.current = "";
                localStorage.setItem("OnlineCourseStatus", "");
                emailLock.current = false;
              });
          });
        }
      });
    } else {
      showNotification(
        "Unable to display course, please check you are not blocking popups.",
        "info"
      );
    }
  };

  return (
    <Container maxWidth="lg">
      <MosaicPlaceholder />
      {isRegistrationFormOpen && (
        <RegistrationForm
          open={isRegistrationFormOpen}
          onClose={handleRegistrationFormClose}
          onSubmit={handleRegistrationFormSubmit}
          selectedCourseName={courseToRegisterFor ? courseToRegisterFor?.name : ""}
        />
      )}

      {isLoginFormOpen && (
        <LoginForm
          open={isLoginFormOpen}
          onClose={handleLoginFormClose}
          onSubmit={startCourse}
          selectedCourse={courseToRegisterFor ? courseToRegisterFor : undefined}
        />
      )}

      {isMobile ?
        <Grid container spacing={2} style={{ marginBottom: "25px", marginTop: "15px" }}>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Partner Training Class")} variant="contained" color="primary" fullWidth>
              Partner Training Class
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Tactical")} variant="contained" color="primary" fullWidth>
              Tactical
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Online Partner Training")} variant="contained" color="primary" fullWidth>
              Online Partner Training
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Mini-Course")} variant="contained" color="primary" fullWidth>
              Mini-Course
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Dispatcher Partner Training")} variant="contained" color="primary" fullWidth>
              Dispatcher Partner Training
            </StyledButton>
          </Grid>
          <Grid size={12} style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <StyledButton onClick={() => scrollIntoView("Rail Tactics (Coming Soon)")} variant="contained" color="primary" fullWidth>
              Rail Tactics
            </StyledButton>
          </Grid>
        </Grid>
        : null}
      <Box sx={theme.custom.pageTitle}>
        <StyledTypography variant="body1" fontSize={"18px"}>
          Welcome to the Online Courses Portal. These courses provide essential training for emergency responders and law enforcement officials working in a rail environment. These courses are self-paced, available 24x7, and free to all Amtrak Police Department (APD) partner first responder agencies – federal, state, local, territorial, and tribal law enforcement to include K-9 and explosive ordnance divisions, transit police and rail security, municipal and county sheriff’s departments, fire departments, dispatchers, emergency medical services, coroners, and emergency management agencies.
        </StyledTypography>
        &nbsp;
      </Box>
      <Grid container spacing={2} sx={{ xs: 12, mt: "-30px" }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Online Partner Training"
            description="A one-hour, self-paced, web-based training course developed as a shortened version of the Partner Training Class. You can take this course at a time and location that is convenient for you."
            registerButtonTitle="REGISTER"
            imageSrc="/images/OnlineCourses.jpg"
            registerLink="/online-course"
            onClick={() => handleRegister("Partner")}
            secondaryButtonAction={() => handleLoginFormOpen("Partner")}
            secondaryButtonTitle={"LOGIN"}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CourseBox
            title="Dispatcher Partner Training"
            description="A one-hour web-based course developed to build public safety dispatch professional’s awareness of railway hazards, first responder safety, and communications and actions necessary during incidents involving trains, stations, or other operations on or near the railroad right-of-way."
            imageSrc="/images/DispatcherTraining.jpg"
            registerLink="/online-course"
            onClick={() => handleRegister("Dispatcher")}
            registerButtonTitle="REGISTER"
            secondaryButtonAction={() => handleLoginFormOpen("Dispatcher")}
            secondaryButtonTitle={"LOGIN"}
          />
        </Grid>
      </Grid>
      <Grid container spacing={4}>

        {isMobile ? (
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              style={{ backgroundColor: "#e7e7e8" }}
              className="p-6"
            >
              <Typography
                style={{ color: "#00537E" }}
                variant="h6"
                className="mb-4 text-gray-700"
              >
                Welcome to the Online Partner Training Portal
              </Typography>
              <Typography
                fontSize="20px"
                variant="body1"
                className="mb-4 text-gray-600"
              >
                This course provides essential training for emergency responders
                and law enforcement officials working in a deadly rail
                environment. Course includes an:
              </Typography>
              <Box
                typography={{ fontSize: "20px" }}
                component="ul"
                className="list-disc pl-6 space-y-2 text-gray-600"
              >
                <li>Operation RAILSAFE</li>
                <li>Amtrak and the Amtrak Police Department</li>
                <li>Intelligence Gathering and Sharing</li>
                <li>Terrorism in a Transportation Environment</li>
                <li>General Railroad Safety</li>
                <li>Arriving on Scene Best Practices</li>
              </Box>
              <Alert severity="info" className="mt-6">
                Course completion time: approximately 1 hour
              </Alert>
            </Paper>
          </Grid>
        ) : null}
      </Grid>
    </Container>
  );
};

export default OnlineCoursePage;